import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { DynamicForm } from "@shared/components/DynamicForm";
import { GenericTable } from "@shared/components/GenericTable";
import { PageHeader } from "@shared/components/PageHeader";
import { FormField, FormService } from "@shared/services/FormServices";
import { TableColumn } from "@shared/types/table";
import { ArrowLeft, Layers, ListCheck, Settings, Sigma } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useCreateReadCycle,
  useGetFrequency,
  useGetMeterSummaryDashboardByRouteId,
  useGetMeterType,
  useGetMonths,
  useGetReadCycleDetailsforEdit,
  useGetRouteDetails,
  useGetRules,
  useGetScheduleReview,
  useGetWeekDays,
  useUpdateReadCycle,
  useUtilityServices,
} from "../../hooks";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { useNavigate, useParams } from "react-router-dom";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { logEvent } from "@shared/analytics/analytics";
import { normalizeDate } from "@shared/utils/dateUtils";

const generateDaysOfMonthOptions = () => {
  const options = [];
  for (let day = 1; day <= 29; day++) {
    options.push({ label: day?.toString(), value: day });
  }
  return options;
};

export default function CreateReadCycle() {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const { action, cycle_id } = useParams();
  const isEditMode = action === "edit" && cycle_id;
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRoutes, setSelectedRoutes] = useState<string[]>([]);
  const [openValidationRules, setOpenValidationRules] = useState(false);
  const [openEstimationRules, setOpenEstimationRules] = useState(false);
  const [validationRules, setValidationRules] = useState({});
  const [estimationRules, setEstimationRules] = useState({});
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [frequency, setFrequency] = useState("");
  const [startDate, setStartDate] = useState("");
  const [time, setTime] = useState("");
  const [endDate, setEndDate] = useState("");

  // Add formData state following the pattern in AddFacility
  const [formData, setFormData] = useState({
    readCycleName: "",
    meterType: "",
    utilityService: [],
    cycleDuration: 30,
    frequency: "",
    start_date: "",
    time: "",
    end_date: "",
    day_of_week: "",
    month_of_year: "",
    day_of_month: "",
  });

  const { data: meterTypeData } = useGetMeterType();
  const { data: utilities } = useUtilityServices();
  const { data: frequencyData } = useGetFrequency();
  const { data: weekDaysData } = useGetWeekDays();
  const { data: monthsData } = useGetMonths();
  const { data: validationRulesData } = useGetRules(0, openValidationRules);
  const { data: estimationRulesData } = useGetRules(1, openEstimationRules);
  const { data: readCycleDetails } = useGetReadCycleDetailsforEdit(
    isEditMode ? cycle_id : undefined
  );
  const createReadCycle = useCreateReadCycle();
  const updateReadCycle = useUpdateReadCycle(cycle_id);
  const { data: scheduleReviewData } = useGetScheduleReview(
    frequency,
    startDate,
    time,
    endDate
  );

  const parseTime = useCallback((t?: string) => {
    if (!t) return "";
    const d = new Date(`1970-01-01T${t}`);
    return isNaN(d.getTime())
      ? ""
      : `${String(d.getHours()).padStart(2, "0")}:${String(
          d.getMinutes()
        ).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`;
  }, []);

  const meterTypeOptions = useMemo(
    () =>
      meterTypeData?.map((meterType) => ({
        value: meterType.key,
        label: meterType.value,
      })),
    [meterTypeData]
  );

  const getUtilityOptions = useMemo(() => {
    if (!utilities || !Array.isArray(utilities)) {
      return [];
    }
    return utilities
      .filter((utility) => utility.isActive === true)
      .map((utility) => ({
        value: utility.name,
        label: utility.name,
      }));
  }, [utilities]);

  const getFrequencyOptions = useMemo(() => {
    if (!frequencyData || !Array.isArray(frequencyData)) {
      return [];
    }
    return frequencyData.map((frequency) => ({
      value: frequency.key,
      label: frequency.value,
    }));
  }, [frequencyData]);

  const getWeekDaysOptions = useMemo(() => {
    if (!weekDaysData || !Array.isArray(weekDaysData)) {
      return [];
    }
    return weekDaysData.map((weekDay) => ({
      value: weekDay.key,
      label: weekDay.value,
    }));
  }, [weekDaysData]);

  const getMonthsOptions = useMemo(() => {
    if (!monthsData || !Array.isArray(monthsData)) {
      return [];
    }
    return monthsData.map((month) => ({
      value: month.key,
      label: month.value,
    }));
  }, [monthsData]);

  const formFields: FormField[] = [
    {
      name: "readCycleName",
      type: "text",
      label: "Read Cycle Name",
      group: "Basic Information",
      disabled: Boolean(isEditMode),
      groupColorNeeded: false,
      groupHelperText: "Core attributes to define your read cycle.",
      placeholder: "e.g., February 2025 Citywide",
      showHelperTooltip: true,
      required: true,
      className: "col-span-1 mt-3",
    },
    {
      name: "meterType",
      type: "select",
      label: "Meter Type",
      group: "Basic Information",
      groupColorNeeded: false,
      options: meterTypeOptions,
      groupHelperText: "Core attributes to define your read cycle.",
      placeholder: "Select meter type...",
      showHelperTooltip: true,
      required: true,
      className: "col-span-1 mt-3",
    },
    {
      name: "utilityService",
      label: "Utility Services",
      type: "multi-select",
      required: true,
      group: "Basic Information",
      groupHelperText: "Core attributes to define your read cycle.",
      groupColorNeeded: false,
      options: getUtilityOptions,
      placeholder: "Select utility services...",
      showHelperTooltip: true,
      className: "col-span-1 mt-3",
    },
    {
      name: "cycleDuration",
      type: "number",
      label: "Cycle Duration(days)",
      group: "Basic Information",
      groupColorNeeded: false,
      groupHelperText: "Core attributes to define your read cycle.",
      defaultValue: 30,
      placeholder: "Enter cycle duration...",
      required: true,
      min: 1,
      max: 365,
      showHelperTooltip: true,
      tooltipText:
        "Meaning: Duration that the reading window remains open. Use: Helps align workforce planning and reading periods. Calculation: Reading period end = start date + duration.",
      className: "col-span-1 mt-3",
    },
    {
      name: "routeSelection",
      label: "",
      type: "custom",
      className: "col-span-2",
      required: false,
      groupColorNeeded: false,
      helperText:
        "The routes displayed above are currently not part of any active read cycle with the same utility service and meter type. If a required route is not visible, it might already be included in another read cycle or not yet created in the system.",
      group: "Routes Selection",
      groupHelperText: "Pick the routes to include in this cycle.",
      customRender: ({ field }: any) => {
        const { data: routeList } = useGetRouteDetails(
          currentPage,
          10,
          undefined,
          undefined,
          undefined,
          searchQuery
        );

        const { data: meterSummaryDashboardData } =
          useGetMeterSummaryDashboardByRouteId(selectedRoutes);
        const routeDetails = routeList?.results;
        const handleSelectionChange = (selectedIds: string[]) => {
          setSelectedRoutes(selectedIds);
        };

        const [pagination, setPagination] = useState({
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: 10,
          startItem: 0,
          endItem: 0,
        });

        // Update pagination based on filtered data
        useEffect(() => {
          const totalItems = routeList?.count || 0;
          const totalPages = Math.ceil(totalItems / 10);
          const startItem = (currentPage - 1) * 10 + 1;
          const endItem = Math.min(currentPage * 10, totalItems);

          setPagination({
            currentPage: currentPage,
            totalPages,
            totalItems,
            itemsPerPage: 10,
            startItem: totalItems > 0 ? startItem : 0,
            endItem: totalItems > 0 ? endItem : 0,
          });
        }, [routeList?.count, currentPage]);

        const handlePageChange = (page: number) => {
          setCurrentPage(page);
        };

        const kpiCards = [
          {
            title: "Routes Selected",
            count: selectedRoutes.length || 0,
            trendSummary:
              "Meaning: Number of routes included in this read cycle. Use: Assess scale and workforce planning.",
            icon: <ListCheck className="h-8 w-8 text-blue-600" />,
          },
          {
            title: "Meters in this Cycle",
            count: meterSummaryDashboardData?.cycleWiseTotal || 0,
            trendSummary: `Assigned ${meterSummaryDashboardData?.totalAssigned} • Unassigned ${meterSummaryDashboardData?.totalUnassigned}`,
            icon: <Layers className="h-8 w-8 text-purple-600" />,
          },
          {
            title: "Meter Conditions",
            count: meterSummaryDashboardData?.cycleWiseTotal || 0,
            trendSummary: `Normal: ${meterSummaryDashboardData?.normalMeters} • Faulty: ${meterSummaryDashboardData?.faultyMeters} • RCNT: ${meterSummaryDashboardData?.rcntMeters}`,
            icon: <Sigma className="h-8 w-8 text-emerald-600" />,
          },
        ];
        const columns: TableColumn<any>[] = [
          {
            key: "routeName",
            header: "Route Name",
            width: "w-15",
            sortable: true,
          },
          {
            key: "areaName",
            header: "Areas",
            sortable: true,
            render: (row) => (
              <>
                {row?.map((area: any, index: number) => (
                  <span key={index}>
                    {area}
                    {index < row.length - 1 && ", "}
                  </span>
                ))}
              </>
            ),
          },
          {
            key: "subAreaName",
            header: "Sub Areas",
            sortable: true,
            render: (row) => (
              <>
                {row?.map((subArea: any, index: number) => (
                  <span key={index}>
                    {subArea}
                    {index < row.length - 1 && ", "}
                  </span>
                ))}
              </>
            ),
          },
          {
            key: "premiseCount",
            header: "Premises",
            sortable: true,
          },
          {
            key: "meterCount",
            header: "Meters",
            sortable: true,
          },
        ];

        const handleSearch = (searchTerm: string) => {
          setSearchQuery(searchTerm);
          setCurrentPage(1); // Reset to first page on new search
        };
        return (
          // Removed nested divs and border wrapper
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {kpiCards.map((card, index) => (
                <QuickStatsCard
                  key={index}
                  title={card.title}
                  count={card.count}
                  trendSummary={card.trendSummary}
                  icon={card.icon}
                  trendDirection={"up"}
                />
              ))}
            </div>
            <GenericTable
              data={routeDetails || []}
              columns={columns}
              loading={false}
              selectable={true}
              selectedRows={selectedRoutes}
              onSelectionChange={handleSelectionChange}
              searchPlaceholder="Search routes..."
              searchParamName="entityQuery"
              onSearch={handleSearch}
              pagination={pagination}
              onPageChange={handlePageChange}
              searchDebounceMs={300}
              rowKey="id"
              emptyMessage={`No data found`}
            />
          </div>
        );
      },
    },
    {
      name: "frequency",
      label: "Frequency",
      type: "select",
      required: false,
      options: getFrequencyOptions,
      disabled: Boolean(isEditMode),
      group: "Schedule Configuration",
      groupHelperText: "Define how and when this cycle should run.",
      groupColorNeeded: false,
      placeholder: "Select frequency...",
      fullWidth: true,
      defaultValue: "",
      helperText: "Choose how often the read cycle should occur",
      showWhen: (formValues: any) => true,
      className: "col-span-1 mt-3",
    },
    {
      name: "start_date",
      label: "Start Date",
      type: "datepicker",
      required: false,
      placeholder: "Select start date...",
      disabled: Boolean(isEditMode),
      group: "Schedule Configuration",
      groupHelperText: "Define how and when this cycle should run.",
      groupColorNeeded: false,
      fullWidth: true,
      defaultValue: "",
      helperText: "Select when the schedule should start",
      className: "col-span-1 mt-3",
    },
    {
      name: "time",
      label: "Start Time",
      type: "time",
      required: false,
      disabled: Boolean(isEditMode),
      placeholder: "Select time...",
      group: "Schedule Configuration",
      groupHelperText: "Define how and when this cycle should run.",
      groupColorNeeded: false,
      fullWidth: true,
      defaultValue: readCycleDetails?.extraData?.time,
      helperText: "Select what time the cycle should run",
      className: "col-span-1 mt-3",
    },
    {
      name: "end_date",
      label: "Schedule End Date(last date to creating new run)",
      type: "datepicker",
      required: false,
      placeholder: "Select end date (optional)...",
      disabled: Boolean(isEditMode),
      group: "Schedule Configuration",
      groupHelperText: "Define how and when this cycle should run.",
      groupColorNeeded: false,
      fullWidth: true,
      defaultValue: "",
      helperText: "Optional: Select when the schedule should end",
      className: "col-span-1 mt-3",
    },
    {
      name: "day_of_week",
      label: "Day of the Week",
      type: "select",
      required: false,
      options: getWeekDaysOptions,
      placeholder: "Select day of week...",
      disabled: Boolean(isEditMode),
      fullWidth: true,
      group: "Schedule Configuration",
      groupHelperText: "Define how and when this cycle should run.",
      groupColorNeeded: false,
      defaultValue: "",
      helperText: "Select which day of the week to run the cycle",
      showWhen: (formValues: any) => {
        return formValues.frequency === "1" || formValues.frequency === "2";
      },
      className: "col-span-1 mt-3",
    },
    {
      name: "month_of_year",
      label: "Month",
      type: "select",
      required: false,
      options: getMonthsOptions,
      placeholder: "Select month...",
      disabled: Boolean(isEditMode),
      fullWidth: true,
      group: "Schedule Configuration",
      groupHelperText: "Define how and when this cycle should run.",
      groupColorNeeded: false,
      defaultValue: "",
      helperText: "Select which month to run the cycle",
      showWhen: (formValues: any) => {
        return (
          formValues.frequency === "6" ||
          formValues.frequency === "7" ||
          formValues.frequency === "8"
        );
      },
      className: "col-span-1 mt-3",
    },
    {
      name: "day_of_month",
      label: "Day of the Month",
      type: "select",
      required: false,
      options: generateDaysOfMonthOptions(),
      placeholder: "Select day of month...",
      disabled: Boolean(isEditMode),
      group: "Schedule Configuration",
      groupHelperText: "Define how and when this cycle should run.",
      groupColorNeeded: false,
      fullWidth: true,
      defaultValue: "",
      helperText: "Select which day of the month to run the cycle (1-29)",
      showWhen: (formValues: any) => {
        return (
          formValues.frequency === "3" ||
          formValues.frequency === "4" ||
          formValues.frequency === "6" ||
          formValues.frequency === "7" ||
          formValues.frequency === "8"
        );
      },
      className: "col-span-1 mt-3",
    },
    {
      name: "preview",
      label:
        "Preview of Next 5 Runs (based on your schedule and cycle duration)",
      type: "custom",
      className: "col-span-2",
      required: false,
      disabled: Boolean(isEditMode),
      groupColorNeeded: false,
      defaultValue: "",
      group: "Schedule Configuration",
      groupHelperText: "Define how and when this cycle should run.",
      customRender: ({ field }: any) => {
        const columns: TableColumn<any>[] = [
          {
            key: "runNumber",
            header: "Run #",
            sortable: true,
          },
          {
            key: "startDatetime",
            header: "Run Date",
            sortable: true,
          },
          {
            key: "remainingTime",
            header: "Remaining Time",
            sortable: true,
          },
          {
            key: "startDatetime",
            header: "Reading Period Start",
            sortable: true,
          },
          {
            key: "endDatetime",
            header: "Reading Window Ends",
            sortable: true,
          },
        ];
        return (
          // Removed nested divs and border wrapper
          <div className="space-y-4">
            <GenericTable
              data={scheduleReviewData || []}
              columns={columns}
              loading={false}
              rowKey="id"
              emptyMessage={`No data found`}
            />
          </div>
        );
      },
    },
    {
      name: "validationRules",
      label: "",
      type: "custom",
      className: "col-span-1",
      required: false,
      groupColorNeeded: false,
      defaultValue: "",
      group: "Validations and Estimations Configurations",
      groupHelperText:
        "Configure validation and estimation rules for meter readings.",
      customRender: ({ field }: any) => {
        return (
          // Removed nested divs and border wrapper
          <div className="space-y-4">
            <Card className="border shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Validation Rules</CardTitle>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setOpenValidationRules(true)}
                    className="h-8 w-8 p-0"
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Available Rules</span>
                  <span className="font-semibold">
                    {validationRulesData?.length}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Applied Rules</span>
                  <span className="font-semibold text-blue-600">
                    {Object.values(validationRules).filter(Boolean).length}
                  </span>
                </div>
              </CardContent>
            </Card>
            <Dialog
              open={openValidationRules}
              onOpenChange={setOpenValidationRules}
            >
              <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-xl">
                    Validation Rules
                  </DialogTitle>
                  <DialogDescription>
                    Enable or disable validation rules to control how readings
                    are validated.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-4">
                  <div className="flex items-center justify-between text-sm mb-4 pb-3 border-b">
                    <span className="text-gray-600">
                      Total Rules:{" "}
                      <span className="font-semibold">
                        {validationRulesData?.length}
                      </span>
                    </span>
                    <span className="text-gray-600">
                      Applied Rules:{" "}
                      <span className="font-semibold text-blue-600">
                        {
                          Object.entries(validationRules).filter(
                            ([key, value]) => value === true
                          ).length
                        }
                      </span>
                    </span>
                  </div>

                  {/* Consumption Check */}
                  {validationRulesData?.map((rule) => (
                    <Card key={rule.name} className="border shadow-sm">
                      <CardContent className="flex items-start justify-between p-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-base mb-1">
                            {rule.name}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {rule.description}
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer ml-4">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={validationRules[rule.name] || false}
                            onChange={(e) =>
                              setValidationRules((prev) => ({
                                ...prev,
                                [rule.name]: e.target.checked,
                              }))
                            }
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <DialogFooter>
                  <Button onClick={() => setOpenValidationRules(false)}>
                    Done
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        );
      },
    },
    {
      name: "estimationRule",
      label: "",
      type: "custom",
      className: "col-span-1",
      required: false,
      groupColorNeeded: false,
      defaultValue: "",
      group: "Validations and Estimations Configurations",
      groupHelperText:
        "Configure validation and estimation rules for meter readings.",
      customRender: ({ field }: any) => {
        return (
          // Removed nested divs and border wrapper
          <div className="space-y-4">
            <Card className="border shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Estimation Rules</CardTitle>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setOpenEstimationRules(true)}
                    className="h-8 w-8 p-0"
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Available Rules</span>
                  <span className="font-semibold">
                    {estimationRulesData?.length}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Applied Rules</span>
                  <span className="font-semibold text-blue-600">
                    {
                      Object.entries(estimationRules).filter(
                        ([key, value]) => value === true
                      ).length
                    }
                  </span>
                </div>
              </CardContent>
            </Card>
            <Dialog
              open={openEstimationRules}
              onOpenChange={setOpenEstimationRules}
            >
              <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-xl">
                    Estimation Rules
                  </DialogTitle>
                  <DialogDescription>
                    Enable, disable and set priority for estimation logics.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-4">
                  <div className="flex items-center justify-between text-sm mb-4 pb-3 border-b">
                    <span className="text-gray-600">
                      Total Rules:{" "}
                      <span className="font-semibold">
                        {estimationRulesData?.length}
                      </span>
                    </span>
                    <span className="text-gray-600">
                      Applied Rules:{" "}
                      <span className="font-semibold text-blue-600">
                        {
                          Object.entries(estimationRules).filter(
                            ([key, value]) => value === true
                          ).length
                        }
                      </span>
                    </span>
                  </div>

                  <div className="rounded-md border border-blue-200 bg-blue-50 text-blue-800 px-4 py-3 text-sm mb-4">
                    When you try to auto complete readings, the system will auto
                    complete readings based on the priority set in the
                    estimation rules below.
                  </div>

                  {/* Historical Average */}
                  {estimationRulesData?.map((rule) => (
                    <Card key={rule.name} className="border shadow-sm">
                      <CardContent className="flex items-start justify-between p-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-base mb-1">
                            {rule.name}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {rule.description}
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer ml-4">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={estimationRules[rule.name] || false}
                            onChange={(e) =>
                              setEstimationRules((prev) => ({
                                ...prev,
                                [rule.name]: e.target.checked,
                              }))
                            }
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <DialogFooter>
                  <Button onClick={() => setOpenEstimationRules(false)}>
                    Done
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        );
      },
    },
  ];

  // Create form instance with formData as defaultValues
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: formData, // Use formData as the default values
  });

  const handleFieldChange = (field: string, value: any) => {
    switch (field) {
      case "frequency":
        setFrequency(value);
        break;
      case "start_date":
        setStartDate(value);
        break;
      case "time":
        setTime(parseTime(value));
        break;
      case "end_date":
        setEndDate(value);
        break;
    }
  };

  useEffect(() => {
    if (isEditMode && readCycleDetails && !isDataLoaded) {
      const data = readCycleDetails;

      // Prepare populated data following the Create component pattern
      const populatedData = {
        readCycleName: data.meterCycleName || "",
        utilityService: data.productCode || [],
        meterType: data.meterType?.toString() || "",
        cycleDuration: data.cycleDuration ? parseInt(data.cycleDuration) : 30,
        frequency: data?.frequency?.toString() || "",
        start_date: normalizeDate(data?.startDate) || "",
        time: data?.extraData?.time,
        end_date: normalizeDate(data?.endDate) || "",
        day_of_week: "",
        month_of_year: "",
        day_of_month: "",
      };

      // Set route selections
      const routeIds = (data.route || []).map((id: any) => String(id));
      setSelectedRoutes(routeIds);

      // Set validation rules
      if (data.validationRules && Array.isArray(data.validationRules)) {
        let newValidationRules = {};
        data.validationRules.forEach((rule) => {
          newValidationRules[rule.name] = Boolean(rule.isActive);
        });
        setValidationRules(newValidationRules);
      }

      // Set estimation rules
      if (data.estimationRules && Array.isArray(data.estimationRules)) {
        let newEstimationRules = {};
        data.estimationRules.forEach((rule) => {
          newEstimationRules[rule.name] = Boolean(rule.isActive);
        });
        setEstimationRules(newEstimationRules);
      }

      // Handle conditional fields based on frequency
      const frequency = data?.frequency;
      if (frequency !== undefined) {
        switch (parseInt(frequency?.toString())) {
          // Daily or Weekly
          case 1:
          case 2:
            if (data?.extraData?.dayOrMonth !== undefined) {
              populatedData.day_of_week =
                data?.extraData?.dayOrMonth?.toString();
            }
            break;

          // Monthly, Quarterly
          case 3:
          case 4:
            if (data?.extraData?.dayOfMonth !== undefined) {
              populatedData.day_of_month =
                data?.extraData?.dayOfMonth?.toString();
            }
            if (data?.extraData?.dayOrMonth !== undefined) {
              populatedData.month_of_year =
                data?.extraData?.dayOrMonth?.toString();
            }
            break;

          // Annually, Bi-annually, Custom
          case 6:
          case 7:
          case 8:
            if (data?.extraData?.dayOrMonth !== undefined) {
              populatedData.month_of_year =
                data?.extraData?.dayOrMonth?.toString();
            }
            if (data?.extraData?.dayOfMonth !== undefined) {
              populatedData.day_of_month =
                data?.extraData?.dayOfMonth?.toString();
            }
            break;
        }
      }

      // Use FormService.populateForm to populate the form
      FormService.populateForm(form, populatedData, formFields);
      setIsDataLoaded(true);
    }
  }, [isEditMode, readCycleDetails, isDataLoaded, form, formFields]);

  const handleSubmit = async () => {
    const formData = form.getValues();
    switch (action) {
      case "add":
        // Build schedule_data object
        const scheduleData: any = {};

        if (formData.frequency) {
          scheduleData.frequency = parseInt(formData.frequency);
        }
        if (formData.time) {
          scheduleData.time = formData.time;
        }
        if (formData.start_date) {
          scheduleData.start_date = formData.start_date;
        }
        if (formData.end_date) {
          scheduleData.end_date = formData.end_date;
        }
        if (formData.time) {
          scheduleData.time = formData.time;
        }
        // Add due_date from start_date
        if (formData.start_date) {
          scheduleData.due_date = formData.start_date;
        }

        // Add end_date if provided
        if (formData.end_date) {
          scheduleData.end_date = formData.end_date;
        }

        // Handle frequency-specific transformations
        const frequency = parseInt(formData.frequency);

        switch (frequency) {
          case 1: // Daily
          case 2: // Weekly
            if (formData.day_of_week) {
              scheduleData.day_or_month = parseInt(formData.day_of_week);
            }
            break;

          case 3: // Monthly
          case 4: // Quarterly
            if (formData.day_of_month) {
              scheduleData.day_of_month = parseInt(formData.day_of_month);
            }
            if (formData.month_of_year) {
              scheduleData.day_or_month = parseInt(formData.month_of_year);
            }
            break;

          case 6: // Annually
          case 7: // Bi-annually
          case 8: // Custom
            if (formData.month_of_year) {
              scheduleData.day_or_month = parseInt(formData.month_of_year);
            }
            break;

          case 9: // Event-based
            if (formData.event) {
              scheduleData.event = formData.event;
            }
            break;

          case 0: // Once
          case 5: // Semi-annually
          default:
            // No additional fields needed for these frequencies
            break;
        }

        // Get enabled validation rule IDs
        const enabledRuleIds = Object.entries(validationRules)
          .filter(([key, value]) => value === true)
          .map(([key]) => {
            const rule = validationRulesData?.find((r) => r.name === key);
            return rule?.id;
          })
          .filter((id) => id !== undefined);

        // Get enabled estimation rule IDs
        const enabledEstimationRuleIds = Object.entries(estimationRules)
          .filter(([key, value]) => value === true)
          .map(([key]) => {
            const rule = estimationRulesData?.find((r) => r.name === key);
            return rule?.id;
          })
          .filter((id) => id !== undefined);

        // Create payload with schedule_data
        const payloadCreate: any = {
          meter_cycle_name: formData.readCycleName,
          product_code: formData.utilityService,
          meter_type: parseInt(formData.meterType),
          cycle_duration: parseInt(formData.cycleDuration),
          route: selectedRoutes,
          remote_utility_id: Number(remoteUtilityId),
          schedule_data: scheduleData,
          validation_rule_ids: enabledRuleIds,
          estimation_rule_ids: enabledEstimationRuleIds,
        };

        createReadCycle.mutateAsync(payloadCreate);
        logEvent("Read Cycle Created", payloadCreate);
        break;

      case "edit":
        // Format validation rules with rule_id and is_active structure
        const formattedValidationRules =
          validationRulesData?.map((rule) => ({
            rule_id: rule.id,
            is_active: validationRules[rule.name] === true,
          })) || [];

        // Format estimation rules with rule_id and is_active structure
        const formattedEstimationRules =
          estimationRulesData?.map((rule) => ({
            rule_id: rule.id,
            is_active: estimationRules[rule.name] === true,
          })) || [];

        // Create payload with formatted rule IDs
        const payloadEdit: any = {
          product_code: formData.utilityService,
          meter_type: parseInt(formData.meterType),
          cycle_duration: parseInt(formData.cycleDuration),
          route: selectedRoutes,
          remote_utility_id: Number(remoteUtilityId),
          validation_rule_ids: formattedValidationRules,
          estimation_rule_ids: formattedEstimationRules,
        };
        updateReadCycle.mutateAsync(payloadEdit);
        logEvent("Read Cycle Edited", payloadEdit);
        break;
    }
  };

  const handleClose = () => {
    navigate("/mx/read-cycle/?tab=readCycles");
  };

  return (
    <div>
      <div className="mb-4 flex items-center gap-4">
        <ArrowLeft
          className="w-5 h-5 cursor-pointer text-gray-700"
          onClick={() => navigate("/mx/read-cycle")}
        />
        <PageHeader
          title={action === "add" ? "Create Read Cycle" : "Edit Read Cycle"}
          subtitle="Configure cycle details, select routes, and schedule occurrences."
        />
      </div>
      <div className="mt-4">
        <DynamicForm
          fields={formFields}
          form={form}
          gridColumns={2}
          responsiveColumns={{ sm: 1, md: 2 }}
          onSubmit={handleSubmit}
          onReset={handleClose}
          onFieldChange={handleFieldChange}
          config={{
            resetButtonText: "Cancel",
            submitButtonText: "Save Read Cycle",
            layout: "grid",
            columns: 2,
            showSubmitButton: true,
            showResetButton: true,
            spacing: "medium",
            classes: {
              container: "w-full mb-4",
              label: "block text-sm font-medium text-gray-700 mb-2",
              buttonContainer: "flex justify-end",
              input:
                "w-full h-11 px-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
              select:
                "w-full h-11 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
              textarea:
                "w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
              error: "text-red-600 text-sm mt-1",
              helperText: "text-xs text-gray-500 mt-1",
              number:
                "w-full h-11 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
              "multi-select":
                "w-full h-11 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
              file:
                " w-full flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-12 text-center " +
                "hover:border-gray-400 transition-colors cursor-pointer bg-white",
              documents:
                "w-full flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-12 text-center " +
                "hover:border-gray-400 transition-colors cursor-pointer bg-white",
            },
          }}
        />
      </div>
    </div>
  );
}
